import { Link, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  BookOpen,
  Code2,
  FileText,
  Download,
  Zap,
  ExternalLink,
  LogIn,
  LayoutGrid,
  Wrench,
} from "lucide-react";

const NAV_SECTIONS = [
  {
    label: "Start here",
    items: [
      { to: "/developers", label: "Overview", icon: <Zap className="w-3.5 h-3.5" />, end: true },
    ],
  },
  {
    label: "Reference",
    items: [
      { to: "/developers/reference", label: "API Reference", icon: <Code2 className="w-3.5 h-3.5" /> },
      { to: "/developers/glossary", label: "Enriched-field glossary", icon: <FileText className="w-3.5 h-3.5" /> },
    ],
  },
  {
    label: "Recipes",
    items: [
      { to: "/developers/recipes/hubspot", label: "HubSpot workflow", icon: <Wrench className="w-3.5 h-3.5" /> },
      { to: "/developers/recipes/pipedrive", label: "Pipedrive automation", icon: <Wrench className="w-3.5 h-3.5" /> },
      { to: "/developers/recipes/salesforce", label: "Salesforce Flow", icon: <Wrench className="w-3.5 h-3.5" /> },
      { to: "/developers/recipes/zapier", label: "Zapier / Make / n8n", icon: <Wrench className="w-3.5 h-3.5" /> },
    ],
  },
  {
    label: "Downloads",
    items: [
      {
        href: "/api/v1/openapi.json",
        label: "OpenAPI 3.1 spec",
        icon: <ExternalLink className="w-3.5 h-3.5" />,
        external: true,
      },
      {
        href: "/api/v1/postman_collection.json",
        label: "Postman collection",
        icon: <Download className="w-3.5 h-3.5" />,
        external: true,
        download: true,
      },
    ],
  },
];

function TopBar() {
  const { user } = useAuth();
  return (
    <header className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-ink-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-2 group">
          <div className="w-7 h-7 rounded-sm bg-ink-900 text-white font-display font-bold text-sm grid place-items-center group-hover:bg-brand-700 transition">
            LiQ
          </div>
          <div className="hidden sm:block">
            <div className="font-display font-bold text-ink-900 text-sm leading-none">LeadIQ Pro</div>
            <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 leading-none mt-0.5">
              Developer docs
            </div>
          </div>
        </Link>
        <div className="flex items-center gap-4 text-sm">
          <Link to="/pricing" className="text-ink-500 hover:text-ink-900 text-xs uppercase tracking-widestx font-mono hidden md:inline">
            Pricing
          </Link>
          {user ? (
            <Link
              to="/settings/api"
              className="inline-flex items-center gap-1.5 text-ink-900 hover:text-brand-700 text-xs uppercase tracking-widestx font-mono"
              data-testid="docs-topbar-settings-link"
            >
              <LayoutGrid className="w-3.5 h-3.5" /> API Settings
            </Link>
          ) : (
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 text-ink-900 hover:text-brand-700 text-xs uppercase tracking-widestx font-mono"
              data-testid="docs-topbar-signin-link"
            >
              <LogIn className="w-3.5 h-3.5" /> Sign in
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}

function SidebarItem({ item }) {
  if (item.external) {
    return (
      <a
        href={item.href}
        target={item.download ? undefined : "_blank"}
        rel="noopener noreferrer"
        {...(item.download ? { download: true } : {})}
        className="group flex items-center gap-2 px-3 py-1.5 rounded-sm text-sm text-ink-500 hover:text-ink-900 hover:bg-ink-50 transition-colors"
      >
        <span className="text-ink-400 group-hover:text-ink-700">{item.icon}</span>
        <span>{item.label}</span>
      </a>
    );
  }
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        `group flex items-center gap-2 px-3 py-1.5 rounded-sm text-sm transition-colors ${
          isActive
            ? "bg-ink-900 text-white font-medium"
            : "text-ink-500 hover:text-ink-900 hover:bg-ink-50"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <span className={isActive ? "text-white" : "text-ink-400 group-hover:text-ink-700"}>
            {item.icon}
          </span>
          <span>{item.label}</span>
        </>
      )}
    </NavLink>
  );
}

export default function DevDocsShell({ children, hideSidebar = false, fullBleed = false }) {
  const loc = useLocation();
  return (
    <div className="min-h-screen bg-ink-50">
      <TopBar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex gap-8 py-8 ${fullBleed ? "" : ""}`}>
          {!hideSidebar && (
            <aside className="w-56 shrink-0 hidden lg:block">
              <nav className="sticky top-20 space-y-5" data-testid="docs-sidebar">
                {NAV_SECTIONS.map((sec) => (
                  <div key={sec.label}>
                    <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 px-3 mb-1.5">
                      {sec.label}
                    </div>
                    <div className="space-y-0.5">
                      {sec.items.map((item) => (
                        <SidebarItem key={item.label} item={item} />
                      ))}
                    </div>
                  </div>
                ))}
              </nav>
            </aside>
          )}
          <main
            className={`flex-1 min-w-0 ${fullBleed ? "" : "bg-white rounded-sm border border-ink-100 p-6 sm:p-10"}`}
            key={loc.pathname}
          >
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}
